import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { partnerRulesApi } from '../../services/api';
import { COLORS, EffectBar, fmt, total, windowLabel } from './shared';

// Partner rules overview (Phase 4a, plan §3.1): every live rule with its effect on both sides,
// totals across them, and the stopped rules. Every rule works both ways.

function Total({ label, value, caption, accent }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 220px', borderColor: accent ? COLORS.hidden_unknown : undefined }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontFamily: 'monospace' }}>
        {fmt(value)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {caption}
      </Typography>
    </Paper>
  );
}

export default function PartnerRulesOverview() {
  const navigate = useNavigate();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showStopped, setShowStopped] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await partnerRulesApi.overview(days));
    } catch (e) {
      setError(e.message);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return <CircularProgress />;

  const owners = new Set(data.rules.map((r) => r.owner_retailer_id)).size;

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h4">Partner rules</Typography>
          <Typography color="text.secondary">
            {data.rules.length} rules on, owned by {owners} retailers. Every rule works both ways.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={days} onChange={(e, d) => d && setDays(d)}>
            {[7, 30, 90].map((d) => (
              <ToggleButton key={d} value={d}>
                {d} days
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Button variant="contained" onClick={() => navigate('/partner-rules/new')}>
            New rule
          </Button>
        </Stack>
      </Stack>

      <Stack direction="row" gap={2} flexWrap="wrap">
        <Total label="Hidden · shopper in the area" value={data.totals.hidden_area} caption="both sides, all rules" />
        <Total
          label="Hidden · we can't locate the shopper"
          value={data.totals.hidden_unknown}
          caption="the part each rule's owner can switch"
          accent
        />
        <Total
          label="Shown although a rule applied"
          value={data.totals.shown + data.totals.shown_unknown}
          caption="shopper outside the area, or not excluded"
        />
      </Stack>

      <Paper variant="outlined">
        <Typography variant="h6" sx={{ p: 2, pb: 0 }}>
          Rules on
        </Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow>
                <TableCell>Rule</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Shoppers we can't locate</TableCell>
                <TableCell>Effect, both sides</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.rules.map((r) => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/partner-rules/${r.id}`)}>
                  <TableCell>
                    <Typography>{r.sentence}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.rule_type === 'catchment_exclusion' ? 'All partners' : 'One partner'} · on since{' '}
                      {new Date(r.effective_from).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                    </Typography>
                  </TableCell>
                  <TableCell>{r.owner_name}</TableCell>
                  <TableCell>{!r.scoped ? '—' : r.unknown_excluded ? 'Excluded too' : 'Not excluded'}</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>
                    <EffectBar buckets={r.effect} height={12} label={r.sentence} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {fmt(total(r.effect))} · {windowLabel({ ...r, days })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {!data.rules.length && (
                <TableRow>
                  <TableCell colSpan={4}>No rules are on.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
        <Stack direction="row" gap={2} flexWrap="wrap" sx={{ p: 2 }}>
          {[
            ['shown', 'Shown'],
            ['hidden_area', 'Hidden · shopper in the area'],
            ['hidden_unknown', "Hidden · can't locate"],
          ].map(([k, l]) => (
            <Stack key={k} direction="row" spacing={0.75} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: COLORS[k] }} />
              <Typography variant="caption">{l}</Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Button onClick={() => setShowStopped(!showStopped)}>
          Stopped rules ({data.stopped.length}) {showStopped ? '▴' : '▾'}
        </Button>
        <Collapse in={showStopped}>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {data.stopped.map((r) => (
              <Stack
                key={r.id}
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/partner-rules/${r.id}`)}
              >
                <Chip size="small" label={r.owner_name} />
                <Typography>{r.sentence}</Typography>
                <Typography variant="body2" color="text.secondary">
                  stopped {new Date(r.effective_until).toLocaleDateString('en-GB')}
                  {r.stop_reason ? ` — ${r.stop_reason}` : ''}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Collapse>
      </Paper>
    </Stack>
  );
}
